import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function AdminWalletsReport() {
  const { t } = useTranslation();

  const [wallets, setWallets] = useState([]);
  const [textFilter, setTextFilter] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/wallets`);
      setWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch admin wallets report", err);
      setWallets([]);
      setMessage("❌ " + t("admin_wallets_report.messages.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const formatMoney = (value) =>
    `${Number(value || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} TL`;

  const translateStatus = (status) => {
    if (!status) return "-";
    return t(`admin_wallets_report.status_values.${status}`, status);
  };

  const getText = (value) => (value == null ? "" : String(value).toLowerCase());

  const filteredWallets = wallets.filter((wallet) => {
    const search = textFilter.toLowerCase().trim();

    return (
      search === "" ||
      getText(wallet.wallet_id).includes(search) ||
      getText(wallet.user_id).includes(search) ||
      getText(wallet.owner_userno).includes(search) ||
      getText(wallet.owner_fullname).includes(search) ||
      getText(wallet.owner_email).includes(search) ||
      getText(wallet.owner_user_tax_id).includes(search) ||
      getText(wallet.route_id).includes(search) ||
      getText(wallet.route_name).includes(search) ||
      getText(wallet.start_point).includes(search) ||
      getText(wallet.end_point).includes(search) ||
      getText(wallet.walletname).includes(search) ||
      getText(wallet.status).includes(search) ||
      getText(wallet.notes).includes(search) ||
      getText(wallet.sponsor).includes(search) ||
      getText(wallet.profile_notes).includes(search) ||
      getText(wallet.vendor_profile_vendor_reserved_notes).includes(search)
    );
  });

  return (
    <StandardScreen
      topNav={<TopNav title={t("admin_wallets_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("admin_wallets_report.title")}>
              <Stack gap="3">
                <FormField label={t("admin_wallets_report.fields.text_filter.label")}>
                  <TextInput
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                    placeholder={t("admin_wallets_report.fields.text_filter.placeholder")}
                  />
                </FormField>
              </Stack>
            </Card>

            <StatusMessage
              message={loading ? t("admin_wallets_report.messages.loading") : message}
            />

            <Card>
              <TableShell stickyColumnCount={2} maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>
                      <Th columnIndex={0} sticky wider>
                        {t("admin_wallets_report.table.wallet_id")}
                      </Th>

                      <Th columnIndex={1} sticky wider>
                        {t("admin_wallets_report.table.walletname")}
                      </Th>

                      <Th wider>{t("admin_wallets_report.table.user_id")}</Th>
                      <Th wider>{t("admin_wallets_report.table.owner_userno")}</Th>
                      <Th wider>{t("admin_wallets_report.table.owner_fullname")}</Th>
                      <Th wider>{t("admin_wallets_report.table.owner_email")}</Th>
                      <Th wider>{t("admin_wallets_report.table.owner_user_tax_id")}</Th>

                      <Th wider>{t("admin_wallets_report.table.route")}</Th>
                      <Th wider>{t("admin_wallets_report.table.start_point")}</Th>
                      <Th wider>{t("admin_wallets_report.table.end_point")}</Th>

                      <Th wider align="right">
                        {t("admin_wallets_report.table.money_balance")}
                      </Th>

                      <Th wider align="right">
                        {t("admin_wallets_report.table.ticket_balance")}
                      </Th>

                      <Th wider>{t("admin_wallets_report.table.sponsor")}</Th>
                      <Th wider>{t("admin_wallets_report.table.status")}</Th>
                      <Th wider>{t("admin_wallets_report.table.created_at")}</Th>
                      <Th wider>{t("admin_wallets_report.table.notes")}</Th>
                      <Th wider>{t("admin_wallets_report.table.profile_notes")}</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredWallets.map((wallet) => (
                      <tr key={wallet.wallet_id || wallet.id}>
                        <Td columnIndex={0} sticky>
                          {wallet.wallet_id || wallet.id}
                        </Td>

                        <Td columnIndex={1} sticky wider>
                          {wallet.walletname || "-"}
                        </Td>

                        <Td>{wallet.user_id || "-"}</Td>
                        <Td>{wallet.owner_userno || "-"}</Td>
                        <Td>{wallet.owner_fullname || "-"}</Td>
                        <Td>{wallet.owner_email || "-"}</Td>
                        <Td>{wallet.owner_user_tax_id || "-"}</Td>

                        <Td>
                          {wallet.route_name
                            ? `#${wallet.route_id} - ${wallet.route_name}`
                            : wallet.route_id
                            ? `#${wallet.route_id}`
                            : "-"}
                        </Td>

                        <Td>{wallet.start_point || "-"}</Td>
                        <Td>{wallet.end_point || "-"}</Td>

                        <NumberTd>{formatMoney(wallet.money_balance)}</NumberTd>
                        <NumberTd>{wallet.ticket_balance ?? 0}</NumberTd>

                        <Td>{wallet.sponsor || "-"}</Td>
                        <Td>{translateStatus(wallet.status)}</Td>
                        <Td>{formatDateTime(wallet.created_at)}</Td>
                        <Td>{wallet.notes || "-"}</Td>
                        <Td>{wallet.profile_notes || "-"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableShell>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}